"use client"

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { getAuthHeader } from "../utils/auth"; // ★ 1. 認証ヘッダー取得関数をインポート
import { useOutletContext, useSearchParams, useNavigate } from "react-router-dom"
import {
    ENGLISH_BETA_LEGAL_SUMMARY_URL,
    ENGLISH_BETA_PRIVACY_URL,
    ENGLISH_BETA_SCTA_URL,
    ENGLISH_BETA_TERMS_URL,
    getEnglishPlanName,
    getEnglishSubscriptionStatus,
    isEnglishBetaSignupSource,
    isEnglishOperatorMode,
} from "../utils/operatorMode";
import { handleStripeNextAction } from "../utils/stripeNextAction";
import {
    getPendingAddonMutation,
    requestAddonMutation,
} from "../utils/addonMutationIdempotency";

const API_URL = ['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? (process.env.REACT_APP_API_URL || "http://localhost:3173")
    : window.location.origin;

const MUTABLE_STRIPE_SUBSCRIPTION_STATUSES = new Set(['active', 'trialing', 'past_due']);
const FRESH_CHECKOUT_REMOTE_STATUSES = new Set(['canceled', 'incomplete_expired']);
const UNAVAILABLE_REMOTE_STATUSES = new Set(['', 'unknown', 'unavailable']);

const normalizeSubscriptionValue = (value) => String(value || '').trim().toLowerCase();

export const getPlanMutationMode = (subscription) => {
    const stripeSubscriptionId = String(subscription?.stripe_subscription_id || '').trim();
    if (!stripeSubscriptionId) return 'fresh_checkout';

    const localStatus = normalizeSubscriptionValue(subscription?.status);
    const planTier = normalizeSubscriptionValue(subscription?.plan_id_string);
    const stripeSyncState = normalizeSubscriptionValue(subscription?.stripe_sync_state);
    const remoteStatus = normalizeSubscriptionValue(subscription?.stripe_remote_status);

    if (stripeSyncState === 'unavailable' || UNAVAILABLE_REMOTE_STATUSES.has(remoteStatus)) {
        return 'blocked';
    }

    if (planTier === 'free' && FRESH_CHECKOUT_REMOTE_STATUSES.has(remoteStatus)) {
        return 'fresh_checkout';
    }

    if (
        MUTABLE_STRIPE_SUBSCRIPTION_STATUSES.has(localStatus)
        && MUTABLE_STRIPE_SUBSCRIPTION_STATUSES.has(remoteStatus)
    ) {
        return 'update';
    }

    return 'blocked';
};

const getPlanMutationBlockedMessage = (subscription) => {
    const stripeSyncState = normalizeSubscriptionValue(subscription?.stripe_sync_state);
    const remoteStatus = normalizeSubscriptionValue(subscription?.stripe_remote_status);
    if (stripeSyncState === 'unavailable' || UNAVAILABLE_REMOTE_STATUSES.has(remoteStatus)) {
        return 'Stripeの最新契約状態を確認できないため、プラン変更を開始できません。契約情報を再読み込みしてから、もう一度お試しください。';
    }
    return '現在のStripe契約状態ではプラン変更を開始できません。契約情報を再読み込みしても解消しない場合は、担当者までお問い合わせください。';
};

const formatPlanAmount = (priceInfo, { monthlyEquivalent = false } = {}) => {
    if (!priceInfo) return "---";
    const rawAmount = Number(monthlyEquivalent ? priceInfo.monthly_equivalent : priceInfo.amount);
    if (!Number.isFinite(rawAmount)) return "---";

    if (String(priceInfo.currency || '').toLowerCase() === 'usd') {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
        }).format(rawAmount / 100);
    }

    return `¥${rawAmount.toLocaleString("ja-JP")}`;
};

// --- ▼▼▼ UI Components (Updated for AccountPage) ▼▼▼ ---

const PeriodSelector = ({ selectedPeriod, setSelectedPeriod, isEnglishBeta = false }) => {
    const periods = isEnglishBeta ? [
        { key: '12_months', label: 'Annual', discount: 'Billed annually' },
        { key: '1_month', label: 'Monthly' },
    ] : [
        { key: '12_months', label: '1年契約', discount: '2ヶ月分お得' },
        { key: '6_months', label: '半年契約', discount: '半月分お得' },
        { key: '1_month', label: '1ヶ月契約' },
    ];

    return (
        <div className="flex justify-center mb-10">
            <div className="bg-slate-200 p-1 rounded-full flex items-center space-x-1 flex-wrap justify-center">
                {periods.map(period => (
                    <button
                        key={period.key}
                        onClick={() => setSelectedPeriod(period.key)}
                        className={`px-4 sm:px-6 py-2 text-sm sm:text-base font-bold rounded-full transition-colors duration-300 relative my-1 ${
                            selectedPeriod === period.key
                                ? 'bg-white text-indigo-600 shadow-md'
                                : 'text-slate-600 hover:bg-slate-300/60'
                        }`}
                    >
                        {period.label}
                        {period.discount && (
                            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                                {period.discount}
                            </span>
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
};

const PlanCard = ({ plan, tier, period, onSelect, isLoading, isSelected, currentSubscription, isEnglishBeta = false }) => {
    const isFreePlan = tier === 'free';
    const currentPriceInfo = plan.prices?.find(p => p.period === period);
    const englishPlanDescriptions = {
        starter: "Entry execution capacity for governed Japan outreach.",
        standard: "Expanded execution capacity for active Japan GTM teams.",
        premium: "Reserved execution capacity for higher-volume operators.",
    };
    
    // ★ 修正: currentPriceInfo が存在するかチェック
    const isCurrentlySubscribed = normalizeSubscriptionValue(currentSubscription?.plan_id_string) === tier
        && (isFreePlan || currentSubscription?.current_stripe_price_id === currentPriceInfo?.id);
    
    let buttonText = isEnglishBeta ? 'Change to this plan' : 'このプランに変更';
    let buttonDisabled = isLoading;

    if (isCurrentlySubscribed) {
        buttonText = isEnglishBeta ? 'Current plan' : '現在のプラン';
        buttonDisabled = true;
    } else if (isLoading && isSelected) {
        buttonText = isEnglishBeta ? 'Processing...' : '処理中...';
    } else if (isFreePlan) {
        buttonText = isEnglishBeta ? 'No free tier' : '無料プランに変更';
    }

    if (!currentPriceInfo && !isFreePlan) {
        return null;
    }

    const features = isEnglishBeta ? {
        starter: ["Governed outreach execution", "Japanese company database access", "Standard execution capacity"],
        standard: ["Governed outreach execution", "Japanese company database access", "Expanded execution capacity"],
        premium: ["Governed outreach execution", "Japanese company database access", "Reserved execution capacity"],
    } : {
        free: ["フォーム営業実行：月500件まで", "リストダウンロード：月100件まで"],
        starter: ["フォーム営業実行：プラン範囲内", "企業リスト利用：プラン範囲内", "送信パワー：標準"],
        standard: ["フォーム営業実行：プラン範囲内", "企業リスト利用：プラン範囲内", "送信パワー：高速"],
        premium: ["フォーム営業実行：プラン範囲内", "企業リスト利用：プラン範囲内", "送信パワー：超高速"],
    };
    const planFeatures = features[tier] || [];

    return (
        <div className={`bg-white rounded-2xl p-6 shadow-lg border-2 flex flex-col relative ${isCurrentlySubscribed ? 'border-green-500' : 'border-slate-200'}`}>
            {isCurrentlySubscribed && <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full absolute top-0 -translate-y-1/2 mx-auto left-0 right-0 w-max">{isEnglishBeta ? "Current plan" : "現在のプラン"}</span>}
            <h3 className="text-xl font-bold text-slate-900">{isEnglishBeta ? getEnglishPlanName(plan.name, tier) : plan.name}</h3>
            <p className="mt-2 text-slate-600 flex-grow">{isEnglishBeta ? (englishPlanDescriptions[tier] || "Governed execution capacity for Japan outreach.") : plan.description}</p>
            <div className="mt-6">
                {isFreePlan ? (
                     <span className="text-4xl lg:text-5xl font-extrabold text-slate-900">{isEnglishBeta ? "No free tier" : "¥0"}</span>
                ) : (
                    <>
                        {/* ★ 修正: currentPriceInfo が存在するかチェック */}
                        <span className="text-4xl lg:text-5xl font-extrabold text-slate-900">
                            {isEnglishBeta ? formatPlanAmount(currentPriceInfo) : `¥${currentPriceInfo?.monthly_equivalent.toLocaleString()}`}
                        </span>
                        <span className="text-base font-medium text-slate-500">{isEnglishBeta ? (currentPriceInfo?.period === '12_months' ? "/year" : "/mo") : "/月"}</span>
                    </>
                )}
                {!isFreePlan && period !== '1_month' && (
                    <p className="text-sm text-slate-500 mt-1">
                        {/* ★ 修正: currentPriceInfo が存在するかチェック */}
                        {isEnglishBeta
                            ? `Billed annually. ${formatPlanAmount(currentPriceInfo, { monthlyEquivalent: true })}/mo equivalent.`
                            : `(一括払い ¥${currentPriceInfo?.amount.toLocaleString()})`}
                    </p>
                )}
            {/* ★★★ ここに </div> を追加しました ★★★ */}
            </div>
            <ul className="mt-4 space-y-3 text-sm text-slate-600 flex-grow">
                {planFeatures.map((feature, index) => (
                    <li key={index} className="flex items-center"><span className="text-indigo-500 mr-2">✓</span>{feature}</li>
                ))}
            </ul>
            <button
                // ★ 修正: currentPriceInfo が存在するかチェック
                onClick={() => onSelect(tier, currentPriceInfo?.id)}
                disabled={buttonDisabled}
                className="mt-8 block w-full text-center bg-indigo-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-indigo-700 disabled:bg-slate-400 disabled:cursor-not-allowed"
            >
                {buttonText}
            </button>
        </div>
    );
};

const formatCurrency = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return "¥0";
    return `¥${amount.toLocaleString("ja-JP")}`;
};

const formatPercent = (value) => {
    const percent = Number(value);
    if (!Number.isFinite(percent)) return "---";
    return `${(percent * 100).toFixed(1)}%`;
};

const formatPoints = (value) => {
    const points = Number(value);
    if (!Number.isFinite(points)) return "0pt";
    return `${points.toLocaleString("ja-JP")}pt`;
};

const normalizeRequestedPointsInput = (value) => {
    if (value == null || value === "") return null;
    const digitsOnly = String(value).replace(/[^\d]/g, "");
    if (!digitsOnly) return null;
    return Number(digitsOnly);
};

const PaymentPointMetricCard = ({ label, value, description }) => (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
        <p className="text-sm font-medium text-slate-500">{label}</p>
        <p className="mt-2 text-2xl font-bold text-slate-900">{value}</p>
        {description ? <p className="mt-2 text-xs text-slate-500 leading-relaxed">{description}</p> : null}
    </div>
);

const getJobSlotBillingPeriodLabel = (periodKey) => {
    switch (periodKey) {
        case "6_months":
            return "半年契約";
        case "12_months":
            return "年間契約";
        case "1_month":
        default:
            return "月額";
    }
};

const buildJobSlotOptionDescription = (subscription) => {
    const unitAmount = Number(subscription?.job_slot_option_unit_amount) || 0;
    const billingPeriodLabel = getJobSlotBillingPeriodLabel(subscription?.current_billing_period);
    const amountLabel = unitAmount > 0 ? formatCurrency(unitAmount) : "金額未設定";
    return `同時実行できるジョブ数を増やし、送信処理を高速化します。(${billingPeriodLabel}${amountLabel} / 1枠)`;
};

const ADDON_MUTATION_LABELS = {
    add_job_slot: "送信処理枠の追加",
    remove_job_slot: "送信処理枠の削除",
    add_marunage: "丸投げオプションの追加",
    remove_marunage: "丸投げオプションの削除",
};

const JobSlotAddConfirmationCard = ({
    currentQuantity,
    subscription,
    isSubmitting,
    onClose,
    onConfirm,
}) => {
    const nextQuantity = currentQuantity + 1;
    const unitAmount = Number(subscription?.job_slot_option_unit_amount) || 0;
    const billingPeriodLabel = getJobSlotBillingPeriodLabel(subscription?.current_billing_period);

    return (
        <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50/70 p-5">
            <p className="text-sm font-semibold text-blue-700">送信処理枠を1枠追加</p>
            <h4 className="mt-1 text-lg font-bold text-slate-900">
                JOB専用追加枠を {currentQuantity}枠から {nextQuantity}枠へ変更します
            </h4>
            <div className="mt-4 space-y-3 rounded-xl border border-blue-100 bg-white p-4 text-sm leading-6 text-slate-700">
                <p>
                    通常料金は1枠あたり{billingPeriodLabel}{unitAmount > 0 ? formatCurrency(unitAmount) : "金額未設定"}です。
                </p>
                <p className="font-medium text-slate-900">
                    今回の即時請求額は、Stripeが現在の契約期間の残日数に基づいて確定します。
                </p>
                <p>
                    JOB枠追加ではポイントを利用しません。保有ポイント残高は変わりません。
                </p>
            </div>

            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <button
                    onClick={onConfirm}
                    disabled={isSubmitting}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:bg-slate-400"
                >
                    {isSubmitting ? "処理中..." : "この内容で1枠追加する"}
                </button>
                <button
                    onClick={onClose}
                    disabled={isSubmitting}
                    className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:bg-slate-100"
                >
                    閉じる
                </button>
            </div>
        </div>
    );
};

const NextInvoiceRedemptionCard = ({
    autoApplyEnabled,
    requestedPointsInput,
    onAutoApplyChange,
    onRequestedPointsChange,
    preview,
    isLoading,
    error,
    isSaving,
    saveMessage,
    subscription,
    onSave,
    isLocked = false,
}) => {
    const isFreePlan = (subscription?.plan_id_string || "free") === "free";
    const eligibleAmount = Number(preview?.breakdown?.eligible_amount_total) || 0;
    const appliedPoints = Number(preview?.simulation?.simulated_applied_points) || 0;
    const cappedPoints = Number(preview?.simulation?.requested_points_capped) || 0;
    const requestedPoints = normalizeRequestedPointsInput(requestedPointsInput);
    const postPayment = Number(preview?.simulation?.post_redemption_payment_amount) || 0;
    const availableBalance = Number(preview?.simulation?.available_points_balance) || 0;
    const canRedeem = Boolean(preview?.eligibility?.can_redeem);

    return (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h3 className="text-lg font-bold text-slate-900">次回請求へのポイント利用予約</h3>
                    <p className="mt-1 text-sm text-slate-600">
                        {isFreePlan
                            ? "無料プラン中は利用できませんが、有料化初回のSaaS請求に使うポイント予約はここで保存できます。"
                            : "SaaS月額と送信枠オプションに対して、次回請求へ使うポイントを事前に予約します。"}
                    </p>
                </div>
                <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                    <input
                        type="checkbox"
                        checked={autoApplyEnabled}
                        onChange={(event) => onAutoApplyChange(event.target.checked)}
                        disabled={isLocked}
                    />
                    自動充当を有効にする
                </label>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <label className="block text-sm font-medium text-slate-700">手動利用ポイント</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        value={requestedPointsInput}
                        onChange={(event) => onRequestedPointsChange(event.target.value)}
                        disabled={autoApplyEnabled || isLocked}
                        placeholder="例: 5000"
                        className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100"
                    />
                    <p className="mt-2 text-xs text-slate-500">
                        手動利用時は、利用可能残高とポイント対象額の範囲に自動で丸められます。
                    </p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
                    <div className="flex justify-between gap-4">
                        <span>利用可能ポイント</span>
                        <strong>{formatPoints(availableBalance)}</strong>
                    </div>
                    <div className="mt-2 flex justify-between gap-4">
                        <span>ポイント対象額</span>
                        <strong>{formatCurrency(eligibleAmount)}</strong>
                    </div>
                    <div className="mt-2 flex justify-between gap-4">
                        <span>次回支払見込み</span>
                        <strong>{formatCurrency(postPayment)}</strong>
                    </div>
                    <div className="mt-2 flex justify-between gap-4">
                        <span>今回適用予定</span>
                        <strong>{formatPoints(appliedPoints)}</strong>
                    </div>
                </div>
            </div>

            {isLoading ? (
                <p className="mt-4 text-sm text-blue-700">次回請求のポイント利用見込みを計算しています...</p>
            ) : null}
            {error ? (
                <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">{error}</p>
            ) : null}

            {preview ? (
                <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${canRedeem ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                            {canRedeem ? "利用可能" : "現在は利用不可"}
                        </span>
                        {preview?.eligibility?.is_first_paid_activation ? (
                            <span className="inline-flex rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700">
                                初回有料化例外あり
                            </span>
                        ) : null}
                    </div>
                    <p className="mt-3 text-sm text-slate-700">
                        {autoApplyEnabled
                            ? `自動充当ONのため、次回請求では ${formatPoints(appliedPoints)} を自動適用予定です。`
                            : `手動利用では ${formatPoints(requestedPoints)} を入力しても、適用予定は ${formatPoints(cappedPoints)} までに自動丸めされます。`}
                    </p>
                    <p className="mt-2 text-sm text-slate-600">
                        次回請求に適用予約すると、保存した設定を今後の preview と checkout 作成時の既定値として使います。
                    </p>
                </div>
            ) : null}

            {saveMessage ? (
                <p className="mt-4 text-sm font-medium text-emerald-700">{saveMessage}</p>
            ) : null}

            <div className="mt-4">
                <button
                    onClick={onSave}
                    disabled={isSaving || isLocked}
                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:bg-slate-400"
                >
                    {isSaving ? "保存中..." : "次回請求に適用予約する"}
                </button>
            </div>
        </div>
    );
};

function AccountPage() {
    const { user } = useOutletContext();
    const isEnglishBetaSurface = isEnglishOperatorMode();
    const navigate = useNavigate();
    const [subscription, setSubscription] = useState(null);
    const [signupSource, setSignupSource] = useState(user?.signup_source || null);
    const [availablePlans, setAvailablePlans] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [portalLoading, setPortalLoading] = useState(false);
    const [isEndingTrial, setIsEndingTrial] = useState(false);
    const [isUpdatingSlots, setIsUpdatingSlots] = useState(false);
    // ★★★ 1. 「丸投げオプション」用のローディングStateを追加 ★★★
    const [isUpdatingMarunageOption, setIsUpdatingMarunageOption] = useState(false);
    const addonMutationInFlightRef = useRef(false);
    const [pendingAddonMutation, setPendingAddonMutation] = useState(() => getPendingAddonMutation());
    const [addonMutationRefreshRequired, setAddonMutationRefreshRequired] = useState(false);
    // ★★★ ここまで ★★★
    const [isPlanChangeMode, setIsPlanChangeMode] = useState(false);
    const [isUpdatingSubscription, setIsUpdatingSubscription] = useState(false);
    const [selectedPlanInfo, setSelectedPlanInfo] = useState({ tier: null, priceId: null });
    const [selectedPeriod, setSelectedPeriod] = useState('1_month');
    const [searchParams, setSearchParams] = useSearchParams();
    const [successMessage, setSuccessMessage] = useState("");
    const [paymentPointsSummary, setPaymentPointsSummary] = useState(null);
    const [isPaymentPointsLoading, setIsPaymentPointsLoading] = useState(false);
    const [paymentPointsError, setPaymentPointsError] = useState("");
    const [isJobSlotAddConfirmationOpen, setIsJobSlotAddConfirmationOpen] = useState(false);
    const [redemptionAutoApplyEnabled, setRedemptionAutoApplyEnabled] = useState(false);
    const [manualRequestedPointsInput, setManualRequestedPointsInput] = useState("");
    const [nextInvoiceRedemptionPreview, setNextInvoiceRedemptionPreview] = useState(null);
    const [nextInvoiceRedemptionError, setNextInvoiceRedemptionError] = useState("");
    const [isNextInvoiceRedemptionLoading, setIsNextInvoiceRedemptionLoading] = useState(false);
    const [isSavingRedemptionSettings, setIsSavingRedemptionSettings] = useState(false);
    const [redemptionSettingsMessage, setRedemptionSettingsMessage] = useState("");
    const isEnglishBeta = isEnglishBetaSurface || isEnglishBetaSignupSource(user?.signup_source || signupSource);
    const isOemUser = Boolean(user?.oem_id);
    const canManageBilling = user?.role === 'admin' || user?.role === 'client_admin';
    const shouldShowDirectBillingControls = canManageBilling && !isEnglishBeta && !isOemUser;
    const currentStripeSubscriptionId = String(subscription?.stripe_subscription_id || '');
    const pendingAddonMutationFromState = pendingAddonMutation
        && String(pendingAddonMutation.subscription_id) === currentStripeSubscriptionId
        ? pendingAddonMutation
        : null;
    const pendingAddonMutationForSubscription = pendingAddonMutationFromState
        || (currentStripeSubscriptionId
            ? getPendingAddonMutation(undefined, currentStripeSubscriptionId)
            : null);
    const isStripeSyncUnavailable = subscription?.stripe_sync_state === 'unavailable';
    const planMutationMode = getPlanMutationMode(subscription);
    const isPlanMutationBlocked = planMutationMode === 'blocked';
    const hasUnresolvedAddonMutation = Boolean(
        pendingAddonMutationForSubscription
        || addonMutationRefreshRequired
    );
    const addonMutationControlsLocked = hasUnresolvedAddonMutation || isStripeSyncUnavailable;
    const planChangeControlsLocked = hasUnresolvedAddonMutation || isPlanMutationBlocked;
    const isUpdatingAddonMutation = isUpdatingSlots || isUpdatingMarunageOption;
    const jobAddInputsLocked = addonMutationControlsLocked;
    const jobSlotQuantity = Math.max(0, Number(subscription?.job_slot_quantity) || 0);

    useEffect(() => {
        const paymentSucceeded = searchParams.get("payment") === "success";
        const freePlanChangeSucceeded = searchParams.get("plan_change") === "free";
        if (paymentSucceeded || freePlanChangeSucceeded) {
            setSuccessMessage(
                freePlanChangeSucceeded
                    ? "無料プランへの変更を受け付けました。"
                    : (isEnglishBeta ? "Checkout completed." : "決済が正常に完了しました！")
            );
            searchParams.delete("payment");
            searchParams.delete("plan_change");
            setSearchParams(searchParams);
            const timer = setTimeout(() => setSuccessMessage(""), 5000);
            return () => clearTimeout(timer);
        }
    }, [searchParams, setSearchParams, isEnglishBeta]);

    const fetchSubscription = useCallback(async () => {
        if (!user || !user.id) return false;
        setIsLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/subscription-status?userId=${user.id}`, {
                headers: getAuthHeader(), // ★ 2. 認証ヘッダーを追加
                cache: "no-store",
            });
            if (!res.ok) {
                const errData = await res.json();
                throw new Error(errData.error || "契約情報の取得に失敗しました。");
            }
            const data = await res.json();
            setSubscription(data.subscription || null);
            setSignupSource(data.signup_source || null);
            setAddonMutationRefreshRequired(false);
            if (isEnglishBetaSignupSource(data.signup_source)) {
                try {
                    const storedUser = JSON.parse(localStorage.getItem("userInfo") || "null");
                    if (storedUser && !isEnglishBetaSignupSource(storedUser.signup_source)) {
                        localStorage.setItem("userInfo", JSON.stringify({
                            ...storedUser,
                            signup_source: data.signup_source,
                        }));
                    }
                    localStorage.setItem("operator_surface", "english_beta");
                } catch (storageError) {
                    // Ignore storage errors in restricted browser contexts.
                }
            }
            return true;
        } catch (err) {
            setError(err.message);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [user]);

    const fetchPaymentPointsSummary = useCallback(async () => {
        if (!user || !user.client_id) return;
        if (!shouldShowDirectBillingControls) {
            setPaymentPointsSummary(null);
            setPaymentPointsError("");
            setIsPaymentPointsLoading(false);
            return;
        }
        setIsPaymentPointsLoading(true);
        setPaymentPointsError("");

        try {
            const res = await fetch(`${API_URL}/api/account/payment-points-summary`, {
                headers: getAuthHeader()
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                throw new Error(data.error || "ポイントサマリーの取得に失敗しました。");
            }
            setPaymentPointsSummary(data);
        } catch (err) {
            setPaymentPointsSummary(null);
            setPaymentPointsError(err.message);
        } finally {
            setIsPaymentPointsLoading(false);
        }
    }, [user, shouldShowDirectBillingControls]);

    useEffect(() => {
        fetchSubscription();
    }, [fetchSubscription]);

    useEffect(() => {
        fetchPaymentPointsSummary();
    }, [fetchPaymentPointsSummary]);

    useEffect(() => {
        const redemptionSettings = paymentPointsSummary?.redemption_settings;
        if (!redemptionSettings) return;
        setRedemptionAutoApplyEnabled(Boolean(redemptionSettings.auto_apply_enabled));
        setManualRequestedPointsInput(
            redemptionSettings.manual_requested_points == null
                ? ""
                : String(redemptionSettings.manual_requested_points)
        );
    }, [paymentPointsSummary?.redemption_settings?.auto_apply_enabled, paymentPointsSummary?.redemption_settings?.manual_requested_points]);

    useEffect(() => {
        if (!shouldShowDirectBillingControls || !user?.client_id || !subscription || isPlanChangeMode || paymentPointsSummary?.enabled === false) return;

        const controller = new AbortController();
        const timer = setTimeout(async () => {
            setIsNextInvoiceRedemptionLoading(true);
            setNextInvoiceRedemptionError("");
            try {
                const res = await fetch(`${API_URL}/api/account/payment-points-redemption-preview`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", ...getAuthHeader() },
                    body: JSON.stringify({
                        auto_apply_enabled: redemptionAutoApplyEnabled,
                        requested_points: redemptionAutoApplyEnabled ? null : normalizeRequestedPointsInput(manualRequestedPointsInput),
                    }),
                    signal: controller.signal,
                });
                const data = await res.json();
                if (!res.ok || !data.success) {
                    throw new Error(data.error || "次回請求のポイント利用見込み取得に失敗しました。");
                }
                setNextInvoiceRedemptionPreview(data);
            } catch (err) {
                if (err.name === "AbortError") return;
                setNextInvoiceRedemptionPreview(null);
                setNextInvoiceRedemptionError(err.message);
            } finally {
                setIsNextInvoiceRedemptionLoading(false);
            }
        }, 300);

        return () => {
            controller.abort();
            clearTimeout(timer);
        };
    }, [shouldShowDirectBillingControls, user?.client_id, subscription?.stripe_subscription_id, subscription?.plan_id_string, isPlanChangeMode, redemptionAutoApplyEnabled, manualRequestedPointsInput, paymentPointsSummary?.enabled]);

    const handleTogglePlanChangeMode = async () => {
        if (!isPlanChangeMode && isEnglishBeta && String(subscription?.status || "").toLowerCase() === "canceled") {
            navigate('/plan-selection?surface=english_beta');
            return;
        }
        if (!isPlanChangeMode && isPlanMutationBlocked) {
            setError(getPlanMutationBlockedMessage(subscription));
            return;
        }
        if (hasUnresolvedAddonMutation) {
            setError("先にオプション変更の受付結果または最新の契約情報を確認してください。");
            return;
        }
        if (!isPlanChangeMode) {
            setIsLoading(true);
            setError("");
            try {
                const res = await fetch(`${API_URL}/api/available-plans?clientId=${user.client_id}${isEnglishBeta ? '&surface=english_beta' : ''}`, {
                    headers: getAuthHeader() // ★ 2. 認証ヘッダーを追加
                });
                const data = await res.json();
                if (!res.ok || !data.success) {
                    throw new Error(data.error || (isEnglishBeta ? "Failed to load available plans." : "プラン情報の取得に失敗しました。"));
                }
                if (!isEnglishBeta && data.plans?.base) {
                    // 2026_09カタログ: プラン変更は組み立て式のプラン選択ページで行う
                    // （旧タイル型モーダルは新カタログのtier構成を表示できないため誘導方式）。
                    navigate('/plan-selection');
                    return;
                }
                setAvailablePlans(data.plans || {});
                setIsPlanChangeMode(true);
            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        } else {
            setIsPlanChangeMode(false);
            setAvailablePlans({});
        }
    };

    const handleUpdateSubscription = async (planTier, newPriceId) => {
        if (hasUnresolvedAddonMutation) {
            setError("先にオプション変更の受付結果または最新の契約情報を確認してください。");
            return;
        }
        if (planTier === 'free' && subscription?.stripe_subscription_id && !isEnglishBeta) {
            navigate('/account/free-plan-survey');
            return;
        }

        setSelectedPlanInfo({ tier: planTier, priceId: newPriceId });
        setIsUpdatingSubscription(true);
        setError("");

        try {
            const mutationMode = getPlanMutationMode(subscription);
            if (mutationMode === 'blocked') {
                throw new Error(getPlanMutationBlockedMessage(subscription));
            }

            // --- 1. Stripe上で変更可能な現行契約がある場合 ---
            if (mutationMode === 'update') {
                // 無料プラン（free）が選ばれたが、Stripe Price IDが渡ってこない場合の補完
                let finalPriceId = newPriceId;
                if (planTier === 'free' && !finalPriceId) {
                    finalPriceId = availablePlans.free?.prices[0]?.id;
                }

                if (!finalPriceId) throw new Error(isEnglishBeta ? "Plan price ID was not found." : "プランの価格IDが見つかりません。");

                const currentPriceId = subscription.current_stripe_price_id || subscription.stripe_price_id || subscription.current_price_id;
                if (currentPriceId && String(currentPriceId) === String(finalPriceId)) {
                    alert(isEnglishBeta ? "This billing option is already active or scheduled." : "既にこの請求条件をご利用中、または変更予約済みです。");
                    return;
                }

                if (!window.confirm(isEnglishBeta ? "Change execution capacity or billing period? Upgrades may apply immediately; downgrades and billing-period changes usually apply from the next renewal period." : "プランまたは請求期間を変更しますか？\n（アップグレードは即時、ダウングレードや請求期間変更は次回更新日から適用される場合があります。トライアル期間中の方が無料プランへ移行する場合は即座に適用となりますので、ご注意ください。）")) {
                    return;
                }

                // 更新API (/api/subscriptions/update) を実行
                const res = await fetch(`${API_URL}/api/subscriptions/update`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                    body: JSON.stringify({
                        subscriptionId: subscription.stripe_subscription_id,
                        newPriceId: finalPriceId,
                        surface: isEnglishBeta ? 'english_beta' : undefined,
                        operator_surface: isEnglishBeta ? 'english_beta' : undefined,
                    }),
                });
                const data = await res.json();
                if (data.requires_action && data.client_secret) {
                    alert(isEnglishBeta ? "Additional payment authentication is required. Please complete it on the next screen." : "カード会社の追加認証が必要です。次の画面で認証を完了してください。");
                    await handleStripeNextAction({
                        clientSecret: data.client_secret,
                        publishableKey: data.publishable_key,
                        isEnglishBeta,
                    });
                    alert(isEnglishBeta ? "Payment authentication was completed. The plan change will be reflected shortly." : "追加認証が完了しました。プラン変更はまもなく反映されます。");
                    setIsPlanChangeMode(false);
                    await new Promise(resolve => setTimeout(resolve, 1500));
                    await fetchSubscription();
                    return;
                }
                if (!res.ok || !data.success) throw new Error(data.error || (isEnglishBeta ? "Failed to change plan." : "プラン変更に失敗しました。"));
                
                alert(isEnglishBeta ? "Plan change request was accepted." : (data.message || "プラン変更を受け付けました。"));
                setIsPlanChangeMode(false);
                await fetchSubscription();
                return;
            }

            // --- 2. Stripe契約がない、または終了済みの場合は新しい決済を開始 ---
            if (planTier === 'free') {
                alert(isEnglishBeta ? "The free plan is already available." : "現在、既に無料プランをご利用中です。");
                return;
            }

            const selectedPlan = availablePlans[planTier];
            if (selectedPlan && window.confirm(isEnglishBeta ? `Open checkout for the ${getEnglishPlanName(selectedPlan.name, planTier)} plan?` : `${selectedPlan.name}プランにアップグレードしますか？`)) {
                const res = await fetch(`${API_URL}/api/create-checkout-session`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...getAuthHeader() }, 
                    body: JSON.stringify({
                        stripePriceId: newPriceId,
                        userId: user.id,
                        planIdString: planTier,
                        surface: isEnglishBeta ? 'english_beta' : undefined,
                        operator_surface: isEnglishBeta ? 'english_beta' : undefined,
                        auto_apply_enabled: redemptionAutoApplyEnabled,
                        requested_points: redemptionAutoApplyEnabled ? null : normalizeRequestedPointsInput(manualRequestedPointsInput),
                    })
                });
                const data = await res.json();
                if (!res.ok || !data.url) throw new Error(data.error || (isEnglishBeta ? "Failed to create checkout session." : "決済ページの作成に失敗しました。"));
                window.location.href = data.url; 
            }

        } catch (err) {
            setError(err.message);
        } finally {
            setIsUpdatingSubscription(false);
            setSelectedPlanInfo({ tier: null, priceId: null });
        }
    };

    const handleManagePaymentInfo = async () => {
        if (addonMutationControlsLocked) {
            setError("先にオプション変更の受付結果または最新の契約情報を確認してください。");
            return;
        }
        setPortalLoading(true);
        setError("");
        try {
            const res = await fetch(`${API_URL}/api/customer-portal`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({
                    userId: user.id,
                    surface: isEnglishBeta ? 'english_beta' : undefined,
                    operator_surface: isEnglishBeta ? 'english_beta' : undefined,
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || (isEnglishBeta ? "Could not open the billing portal." : "管理ページの起動に失敗しました。"));
            window.location.href = data.url;
        } catch (err) {
            setError(err.message);
            setPortalLoading(false);
        }
    };

    const handleEndTrialEarly = async () => {
        if (!subscription || !subscription.stripe_subscription_id || addonMutationControlsLocked) return;
        if (!window.confirm("無料トライアルを終了し、ただちに有料プランへ移行しますか？")) return;
        setIsEndingTrial(true);
        setError("");
        try {
            const res = await fetch(`${API_URL}/api/end-trial-early`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({ subscriptionId: subscription.stripe_subscription_id }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "有料プランへの移行に失敗しました。");
            alert(data.message);
            fetchSubscription();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsEndingTrial(false);
        }
    };

    const closeJobSlotAddConfirmation = () => {
        setIsJobSlotAddConfirmationOpen(false);
    };

    const handleOpenJobSlotAddConfirmation = () => {
        if (
            !subscription
            || !subscription.stripe_subscription_id
            || addonMutationControlsLocked
            || addonMutationInFlightRef.current
        ) return;
        setError("");
        setIsJobSlotAddConfirmationOpen(true);
    };

    const performAddonMutation = async ({ operation }) => {
        if (!subscription || !subscription.stripe_subscription_id || addonMutationInFlightRef.current) return;
        const isJobSlotOperation = operation === 'add_job_slot' || operation === 'remove_job_slot';
        addonMutationInFlightRef.current = true;
        if (isJobSlotOperation) {
            setIsUpdatingSlots(true);
        } else {
            setIsUpdatingMarunageOption(true);
        }
        setError("");
        try {
            const data = await requestAddonMutation({
                apiUrl: API_URL,
                operation,
                subscriptionId: subscription.stripe_subscription_id,
                authHeaders: getAuthHeader(),
            });
            setAddonMutationRefreshRequired(true);
            setPendingAddonMutation(null);
            alert(data.message);
            if (operation === 'add_job_slot') closeJobSlotAddConfirmation();
            const subscriptionRefreshed = await fetchSubscription();
            if (!subscriptionRefreshed) {
                setAddonMutationRefreshRequired(true);
                setError("オプション変更は完了しましたが、最新の契約情報を取得できませんでした。再読み込みが完了するまで次の請求操作はできません。");
            }
        } catch (err) {
            if (err.retryWithSameIntent && err.addonMutationIntent) {
                setPendingAddonMutation(err.addonMutationIntent);
                setError(`${err.message || "オプションの更新結果を確認できませんでした。"} 前回と同じ操作内容で受付結果を再確認してください。`);
            } else {
                setPendingAddonMutation(null);
                setError(err.message);
            }
        } finally {
            addonMutationInFlightRef.current = false;
            if (isJobSlotOperation) {
                setIsUpdatingSlots(false);
            } else {
                setIsUpdatingMarunageOption(false);
            }
        }
    };

    const handleAddSlot = async () => {
        if (addonMutationControlsLocked) return;
        await performAddonMutation({ operation: 'add_job_slot' });
    };

    const handleSaveRedemptionSettings = async () => {
        if (jobAddInputsLocked) return;
        setIsSavingRedemptionSettings(true);
        setRedemptionSettingsMessage("");
        setError("");
        try {
            const requestedPoints = normalizeRequestedPointsInput(manualRequestedPointsInput);
            const res = await fetch(`${API_URL}/api/account/payment-points-redemption-settings`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({
                    auto_apply_enabled: redemptionAutoApplyEnabled,
                    requested_points: requestedPoints,
                }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                throw new Error(data.error || "ポイント利用設定の保存に失敗しました。");
            }
            setRedemptionSettingsMessage("次回請求に使うポイント設定を保存しました。");
            await fetchPaymentPointsSummary();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSavingRedemptionSettings(false);
        }
    };

    const handleRemoveSlot = async () => {
        if (
            !subscription
            || !subscription.stripe_subscription_id
            || jobSlotQuantity <= 0
            || addonMutationControlsLocked
            || addonMutationInFlightRef.current
        ) return;
        if (!window.confirm("送信処理枠追加オプションを1つ即時削除しますか？\n\nこの操作でただちに削除されます。既に請求済みの料金の返金・日割り精算はありません。")) return;
        await performAddonMutation({ operation: 'remove_job_slot' });
    };

    // ★★★ 2. 「丸投げオプション」用のハンドラ関数を新設 ★★★
    const handleAddMarunageOption = async () => {
        if (
            !subscription
            || !subscription.stripe_subscription_id
            || addonMutationControlsLocked
            || addonMutationInFlightRef.current
        ) return;
        if (!window.confirm("丸投げサポート＆営業ツール制作オプション（月額50,000円）を追加しますか？\n\n本日分の日割り料金が即時課金され、次回以降はプラン料金と合算して請求されます。")) return;
        await performAddonMutation({ operation: 'add_marunage' });
    };

    const handleRemoveMarunageOption = async () => {
        if (
            !subscription
            || !subscription.stripe_subscription_id
            || addonMutationControlsLocked
            || addonMutationInFlightRef.current
        ) return;
        if (!window.confirm("丸投げサポート＆営業ツール制作オプションを即時削除しますか？\n\nこの操作でただちに削除されます。既に請求済みの料金の返金・日割り精算はありません。")) return;
        await performAddonMutation({ operation: 'remove_marunage' });
    };

    const handleRetryPendingAddonMutation = async () => {
        if (!pendingAddonMutationForSubscription) return;
        await performAddonMutation({
            operation: pendingAddonMutationForSubscription.operation,
        });
    };

    const handleReloadSubscriptionAfterMutation = async () => {
        setError("");
        const refreshed = await fetchSubscription();
        if (!refreshed) setAddonMutationRefreshRequired(true);
    };
    // ★★★ ここまで ★★★


    const formatDate = (dateString) => {
        if (!dateString) return "---";
        const date = new Date(dateString);
        return date.toLocaleDateString("ja-JP", { year: "numeric", month: "long", day: "numeric" });
    };

    const planOrder = isEnglishBeta ? ['starter'] : ['free', 'starter', 'standard', 'premium'];
    const planChangeGridClassName = isEnglishBeta
        ? "grid grid-cols-1 gap-8 max-w-xl mx-auto"
        : "grid grid-cols-1 md:grid-cols-2 gap-8";
    const paymentPointMetrics = useMemo(() => {
        const monthly = paymentPointsSummary?.monthly;
        const current = paymentPointsSummary?.current;
        return [
            {
                label: "ポイント対象売上",
                value: formatCurrency(monthly?.connect_revenue),
                description: "Stripe Connect のカード決済でポイント計算の対象になった今月売上です。",
            },
            {
                label: "現在手数料",
                value: formatPercent(current?.fee_percent),
                description: "現在 client_settings に設定されている適用手数料率です。",
            },
            {
                label: "今月獲得ポイント",
                value: formatPoints(monthly?.total_points_awarded),
                description: "決済ポイントと行動ポイントの当月合計です。",
            },
            {
                label: "利用可能ポイント残高",
                value: formatPoints(monthly?.available_points_balance),
                description: "現在利用可能な累計ポイント残高です。",
            },
            {
                label: "実質SaaS負担",
                value: formatCurrency(monthly?.effective_saas_cost_this_month),
                description: "今月のSaaS料金から利用可能ポイントを充当した後の見込み負担額です。",
            },
        ];
    }, [paymentPointsSummary]);

    const nextStageMessage = useMemo(() => {
        const nextStage = paymentPointsSummary?.next_stage;
        if (!nextStage || !nextStage.next_stage_code) {
            return "現在の手数料帯が最終段階です。";
        }
        return `あと ${formatCurrency(nextStage.remaining_to_threshold)} のポイント対象売上で ${formatPercent(nextStage.next_fee_percent)} に下がります。`;
    }, [paymentPointsSummary]);

    const nextStageSubMessage = useMemo(() => {
        const nextStage = paymentPointsSummary?.next_stage;
        if (!nextStage || !nextStage.next_stage_code) {
            return "次の到達ラインはありません。";
        }
        return `到達ライン: ${formatCurrency(nextStage.threshold_amount)} / 次ステージ: ${nextStage.next_stage_code}`;
    }, [paymentPointsSummary]);

    if (isLoading && !isPlanChangeMode) {
        return <div className="p-8 text-center">{isEnglishBeta ? "Loading account information..." : "情報を読み込んでいます..."}</div>;
    }

    const normalizedSubscriptionStatus = String(subscription?.status || "").toLowerCase();
    const isDisplayedAsFreePlan = normalizeSubscriptionValue(subscription?.plan_id_string) === 'free';
    const isEnglishBetaCanceled = isEnglishBeta && normalizedSubscriptionStatus === "canceled";
    const canManageEnglishBetaBilling = canManageBilling && isEnglishBeta && Boolean(subscription?.stripe_subscription_id) && ["active", "trialing", "past_due"].includes(normalizedSubscriptionStatus);
    const canChangeEnglishBetaCapacity = canManageBilling && isEnglishBeta && Boolean(subscription) && subscription?.plan_id_string !== "free" && ["active", "trialing", "past_due"].includes(normalizedSubscriptionStatus);

    return (
        <div className="p-3 sm:p-8 bg-slate-50 min-h-screen">
            {successMessage && <div className="fixed left-3 right-3 top-5 sm:left-auto sm:right-5 bg-green-500 text-white px-4 sm:px-6 py-3 rounded-lg shadow-lg animate-fade-in-out z-50 text-sm sm:text-base">{successMessage}</div>}
            <div className="max-w-4xl mx-auto">
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6 sm:mb-8">{isEnglishBeta ? "Account" : "アカウント・契約管理"}</h1>
                {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">{error}</div>}
                <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-8 space-y-8 sm:space-y-10">
                    {isPlanChangeMode ? (
                        <div>
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6 border-b pb-4">
                                <h2 className="text-xl font-bold text-slate-800">{isEnglishBeta ? "Change plan" : "プランを変更する"}</h2>
                                <button onClick={handleTogglePlanChangeMode} className="w-full sm:w-auto rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-slate-50">{isEnglishBeta ? "Cancel" : "キャンセル"}</button>
                            </div>
                            <PeriodSelector selectedPeriod={selectedPeriod} setSelectedPeriod={setSelectedPeriod} isEnglishBeta={isEnglishBeta} />
                            
                            <div className={planChangeGridClassName}>
                                {planOrder
                                    .filter(tier => availablePlans[tier])
                                    .map(tier => {
                                        const plan = availablePlans[tier];
                                        const currentPriceInfo = plan.prices?.find(p => p.period === selectedPeriod);
                                        if(!currentPriceInfo && tier !== 'free') return null; 

                                        return (
                                            <PlanCard
                                                key={tier}
                                                plan={plan}
                                                tier={tier}
                                                period={selectedPeriod}
                                                onSelect={handleUpdateSubscription}
                                                isLoading={isUpdatingSubscription}
                                                isSelected={selectedPlanInfo.priceId === currentPriceInfo?.id}
                                                currentSubscription={subscription}
                                                isEnglishBeta={isEnglishBeta}
                                            />
                                        )
                                    })
                                }
                            </div>
                            {isEnglishBeta && (
                                <div className="mx-auto mt-8 max-w-xl rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 text-center text-sm leading-6 text-slate-600">
                                    Need higher-volume execution? Standard and Premium capacity are handled through Help & Support during the English workspace rollout.
                                    <a href="/app/help?surface=english_beta" className="ml-1 font-bold text-indigo-600 underline hover:text-indigo-700">Open support</a>.
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            <div>
                                <h2 className="text-xl font-bold text-slate-800 mb-6 border-b pb-4">{isEnglishBeta ? "Current plan and capacity" : "現在の契約状況"}</h2>
                                {subscription ? (
                                    <div className="space-y-3 text-slate-700 text-base">
                                        {isDisplayedAsFreePlan ? (
                                            <>
                                                <p><strong>{isEnglishBeta ? "Current plan:" : "現在のプラン:"}</strong> <span className="font-semibold text-indigo-600">{isEnglishBeta ? "No active paid operator plan" : "無料プラン"}</span></p>
                                                <p className="text-sm text-slate-600">{isEnglishBeta ? "English Operator Beta is paid-only. Choose execution capacity to continue." : "基本的な機能をご利用いただけます。全ての機能を利用するには、有料プランへのアップグレードをご検討ください。"}</p>
                                                {canManageBilling && <div className="pt-4">
                                                    <button onClick={handleTogglePlanChangeMode} disabled={planChangeControlsLocked} className="w-full sm:w-auto bg-green-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-green-700 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed">{isEnglishBeta ? "Upgrade execution capacity" : "有料プランにアップグレードする"}</button>
                                                </div>}
                                            </>
                                        ) : (
                                            /* ★有料プラン表示エリアをフラグメントで囲んで警告を追加 */
                                            <>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                                                    <p><strong>{isEnglishBeta ? "Current plan:" : "現在のプラン:"}</strong></p>
                                                    <p><span className="font-semibold text-indigo-600 text-lg">{isEnglishBeta ? getEnglishPlanName(subscription.plan_name, subscription.plan_id_string) : (subscription.plan_name || "---")}</span></p>
                                                    
                                                    <p><strong>{isEnglishBeta ? "Status:" : "ステータス:"}</strong></p>
                                                    <p><span className={`font-semibold px-2 py-0.5 rounded-full text-xs ${subscription.status === 'active' ? 'bg-green-100 text-green-800' : subscription.status === 'trialing' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}`}>{isEnglishBeta ? getEnglishSubscriptionStatus(subscription.status) : (subscription.status || "不明")}</span></p>
                                                    
                                                    <p><strong>{isEnglishBeta ? "Additional execution lanes:" : "送信処理枠追加オプション:"}</strong></p>
                                                    <p><span className="font-semibold text-indigo-600">{jobSlotQuantity}{isEnglishBeta ? "" : "個"}</span></p>
                                                    
                                                    <p><strong>{isEnglishBeta ? "Contract start date:" : "契約開始日:"}</strong></p>
                                                    <p>{formatDate(subscription.created_at)}</p>
                                                </div>

                                                {canChangeEnglishBetaCapacity && (
                                                    <div className="pt-4">
                                                        <button onClick={handleTogglePlanChangeMode} className="w-full sm:w-auto bg-indigo-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-indigo-700 transition-colors">
                                                            Change execution capacity
                                                        </button>
                                                    </div>
                                                )}

                                                {canManageBilling && isEnglishBetaCanceled && (
                                                    <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-5">
                                                        <h4 className="text-sm font-bold text-amber-900">Account inactive</h4>
                                                        <p className="mt-2 text-sm leading-6 text-amber-800">
                                                            This English Operator Beta subscription has been canceled. Choose execution capacity to resume. Returning canceled accounts should not expect an additional operator trial.
                                                        </p>
                                                        <button
                                                            onClick={() => navigate('/plan-selection?surface=english_beta')}
                                                            className="mt-4 w-full sm:w-auto rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800"
                                                        >
                                                            Resume English Operator Beta
                                                        </button>
                                                    </div>
                                                )}

                                                {subscription.status === 'past_due' && (
                                                    <div className="mt-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg shadow-sm flex items-start">
                                                        <div className="flex-shrink-0 text-red-500 mr-3">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                            </svg>
                                                        </div>
                                                        <div>
                                                            <h4 className="text-sm font-bold text-red-800">{isEnglishBeta ? "Payment confirmation required" : "お支払いの確認が取れていません"}</h4>
                                                            <p className="mt-1 text-sm text-red-700 leading-relaxed">
                                                                {isEnglishBeta ? (
                                                                    <>
                                                                        Your previous payment could not be confirmed. Update your payment method to keep execution available.
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        前回の決済に失敗したため、現在支払い遅延の状態です。
                                                                        カードの有効期限切れや限度額オーバーの可能性があります。
                                                                        <br />
                                                                        <strong>下の「お支払い情報を管理する」ボタンから有効なカードを再登録</strong>
                                                                        いただくことで、自動的に再決済が実行され、制限が解除されます。
                                                                    </>
                                                                )}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <div>
                                        <p>{isEnglishBeta ? "No active plan is currently available." : "現在、有効な契約はありません。"}</p>
                                        {canManageBilling && <div className="pt-4"><button onClick={() => navigate(isEnglishBeta ? '/plan-selection?surface=english_beta' : '/plan-selection')} className="w-full sm:w-auto bg-blue-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-blue-700 transition-colors">{isEnglishBeta ? "Choose execution capacity" : "プランを選択する"}</button></div>}
                                    </div>
                                )}
                            </div>

                            {shouldShowDirectBillingControls && subscription?.stripe_subscription_id && isPlanMutationBlocked && (
                                <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
                                    <p className="font-semibold text-amber-900">新しい決済を安全に開始できない状態です。</p>
                                    <p className="mt-2 text-sm leading-6 text-amber-800">{getPlanMutationBlockedMessage(subscription)}</p>
                                    <button
                                        type="button"
                                        onClick={handleReloadSubscriptionAfterMutation}
                                        disabled={isLoading}
                                        className="mt-3 rounded-lg bg-amber-700 px-4 py-2 text-sm font-bold text-white hover:bg-amber-800 disabled:bg-slate-400"
                                    >
                                        契約情報を再読み込み
                                    </button>
                                </div>
                            )}

                            {shouldShowDirectBillingControls && <div>
                                <h2 className="text-xl font-bold text-slate-800 mb-6 border-b pb-4">決済ポイント概要</h2>
                                {isPaymentPointsLoading ? (
                                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">
                                        ポイント情報を読み込んでいます...
                                    </div>
                                ) : paymentPointsError ? (
                                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
                                        {paymentPointsError}
                                    </div>
                                ) : paymentPointsSummary?.enabled === false ? (
                                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
                                        決済ポイントは直販の日本語SaaSアカウントのみ対象です。
                                    </div>
                                ) : (
                                    <div className="space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                            {paymentPointMetrics.map((metric) => (
                                                <PaymentPointMetricCard
                                                    key={metric.label}
                                                    label={metric.label}
                                                    value={metric.value}
                                                    description={metric.description}
                                                />
                                            ))}
                                        </div>

                                        <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-6">
                                            <p className="text-sm font-medium text-indigo-700">次の到達ライン</p>
                                            <p className="mt-2 text-xl font-bold text-indigo-950">{nextStageMessage}</p>
                                            <p className="mt-2 text-sm text-indigo-700">{nextStageSubMessage}</p>
                                            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                                                <div className="rounded-lg bg-white/80 px-4 py-3 border border-indigo-100">
                                                    <p className="text-indigo-600">現在ステージ</p>
                                                    <p className="mt-1 font-semibold text-slate-900">{paymentPointsSummary?.current?.stage_code || "---"}</p>
                                                </div>
                                                <div className="rounded-lg bg-white/80 px-4 py-3 border border-indigo-100">
                                                    <p className="text-indigo-600">還元テーブル上限1</p>
                                                    <p className="mt-1 font-semibold text-slate-900">{formatCurrency(paymentPointsSummary?.tiers?.base_cap_amount)}</p>
                                                </div>
                                                <div className="rounded-lg bg-white/80 px-4 py-3 border border-indigo-100">
                                                    <p className="text-indigo-600">還元テーブル上限2</p>
                                                    <p className="mt-1 font-semibold text-slate-900">{formatCurrency(paymentPointsSummary?.tiers?.tier2_cap_amount)}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>}

                            {shouldShowDirectBillingControls && paymentPointsSummary?.enabled !== false && subscription && subscription.stripe_subscription_id && (
                                <NextInvoiceRedemptionCard
                                    autoApplyEnabled={redemptionAutoApplyEnabled}
                                    requestedPointsInput={manualRequestedPointsInput}
                                    onAutoApplyChange={setRedemptionAutoApplyEnabled}
                                    onRequestedPointsChange={setManualRequestedPointsInput}
                                    preview={nextInvoiceRedemptionPreview}
                                    isLoading={isNextInvoiceRedemptionLoading}
                                    error={nextInvoiceRedemptionError}
                                    isSaving={isSavingRedemptionSettings}
                                    saveMessage={redemptionSettingsMessage}
                                    subscription={subscription}
                                    onSave={handleSaveRedemptionSettings}
                                    isLocked={jobAddInputsLocked}
                                />
                            )}
                            
                            {shouldShowDirectBillingControls && subscription && subscription.status === "trialing" && (
                                <div>
                                    <h2 className="text-xl font-bold text-slate-800 mb-4 border-b pb-4">トライアル管理</h2>
                                    <div className="space-y-6">
                                        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                            <h4 className="font-semibold text-green-800">有料プランへ即時移行</h4>
                                            <p className="text-sm text-green-700 mt-1">無料トライアルをスキップして、すぐに有料プランを開始します。</p>
                                            <button onClick={handleEndTrialEarly} disabled={isEndingTrial || addonMutationControlsLocked} className="mt-3 inline-flex w-full sm:w-auto items-center justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:bg-gray-400">{isEndingTrial ? "処理中..." : "今すぐ有料プランを開始"}</button>
                                        </div>
                                        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                                            <h4 className="font-semibold text-red-800">トライアルのキャンセル</h4>
                                            <p className="text-sm text-red-700 mt-1">トライアル期間の終了後、有料プランへ移行しない場合は、お手数ですがアカウント管理よりプラン変更で無料プランへの移行をお願いいたします。</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {shouldShowDirectBillingControls && subscription && subscription.stripe_subscription_id && (
                                <div>
                                    <h2 className="text-xl font-bold text-slate-800 mb-6 border-b pb-4">各種お手続き</h2>

                                    {pendingAddonMutationForSubscription && (
                                        <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4">
                                            <p className="font-semibold text-amber-900">
                                                前回の{ADDON_MUTATION_LABELS[pendingAddonMutationForSubscription.operation] || "オプション変更"}は受付結果を確認中です。
                                            </p>
                                            <p className="mt-2 text-sm leading-6 text-amber-800">
                                                二重変更を防ぐため、保存済みの同じ操作内容・操作キーで結果を確認するまで、別の請求操作はできません。
                                            </p>
                                            <button
                                                type="button"
                                                onClick={handleRetryPendingAddonMutation}
                                                disabled={isUpdatingAddonMutation}
                                                className="mt-3 rounded-lg bg-amber-700 px-4 py-2 text-sm font-bold text-white hover:bg-amber-800 disabled:bg-slate-400"
                                            >
                                                {isUpdatingAddonMutation ? "確認中..." : "同じ内容で受付結果を再確認"}
                                            </button>
                                        </div>
                                    )}

                                    {!pendingAddonMutationForSubscription && (addonMutationRefreshRequired || isStripeSyncUnavailable) && (
                                        <div className="mb-6 rounded-xl border border-red-300 bg-red-50 p-4">
                                            <p className="font-semibold text-red-900">
                                                {isStripeSyncUnavailable
                                                    ? "Stripeの最新契約状態を確認できていません。"
                                                    : "最新の契約情報を確認できていません。"}
                                            </p>
                                            <p className="mt-2 text-sm leading-6 text-red-800">
                                                二重変更を防ぐため、Stripeを含む契約情報の再読み込みが完了するまでオプションの追加・削除を停止しています。
                                            </p>
                                            <button
                                                type="button"
                                                onClick={handleReloadSubscriptionAfterMutation}
                                                disabled={isLoading}
                                                className="mt-3 rounded-lg bg-red-700 px-4 py-2 text-sm font-bold text-white hover:bg-red-800 disabled:bg-slate-400"
                                            >
                                                契約情報を再読み込み
                                            </button>
                                        </div>
                                    )}
                                    
                                    <div className="bg-slate-50 p-4 sm:p-6 rounded-lg border border-slate-200 mb-6">
                                        <h3 className="font-semibold text-slate-800">プラン変更</h3>
                                        <p className="text-sm text-slate-500 mt-1 mb-4">現在のプランをアップグレードまたはダウングレードします。</p>
                                        <button onClick={handleTogglePlanChangeMode} disabled={planChangeControlsLocked} className="w-full sm:w-auto bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 disabled:bg-slate-400">プランを変更する</button>
                                    </div>
                                    
                                    <div className="bg-slate-50 p-4 sm:p-6 rounded-lg border border-slate-200 mb-6">
                                        <h3 className="font-semibold text-slate-800">送信処理枠追加オプション</h3>
                                        <p className="text-sm text-slate-500 mt-1 mb-4">{buildJobSlotOptionDescription(subscription)}</p>
                                        <p className="mb-4 text-sm font-medium text-slate-700">
                                            現在のJOB専用追加枠: {jobSlotQuantity}枠
                                        </p>
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                            {jobSlotQuantity > 0 && (
                                                <button onClick={handleRemoveSlot} disabled={isUpdatingAddonMutation || addonMutationControlsLocked} className="w-full sm:w-auto bg-white text-red-600 border border-red-300 px-4 py-2 rounded-md text-sm font-medium hover:bg-red-50 disabled:bg-gray-400">{isUpdatingSlots ? "処理中..." : "－ 1枠削除する"}</button>
                                            )}
                                            <button onClick={handleOpenJobSlotAddConfirmation} disabled={isUpdatingAddonMutation || addonMutationControlsLocked} className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 disabled:bg-gray-400">＋ 1枠追加内容を確認する</button>
                                        </div>
                                        {isJobSlotAddConfirmationOpen && (
                                            <JobSlotAddConfirmationCard
                                                currentQuantity={jobSlotQuantity}
                                                subscription={subscription}
                                                isSubmitting={isUpdatingAddonMutation || addonMutationControlsLocked}
                                                onClose={closeJobSlotAddConfirmation}
                                                onConfirm={handleAddSlot}
                                            />
                                        )}
                                    </div>

                                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 sm:p-6 rounded-lg border border-indigo-200 mb-6 shadow-lg">
                                        <h3 className="font-semibold text-indigo-800">丸投げサポート＆営業ツール制作</h3>
                                        <p className="text-sm text-indigo-700 mt-1 mb-4">戦略立案、LP・営業資料制作、SalesBreaker全運営代行をまとめて行います。(月額50,000円 / +2枠)</p>
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                            {subscription.has_marunage_option ? (
                                                <button onClick={handleRemoveMarunageOption} disabled={isUpdatingAddonMutation || addonMutationControlsLocked} className="w-full sm:w-auto bg-white text-red-600 border border-red-300 px-4 py-2 rounded-md text-sm font-medium hover:bg-red-50 disabled:bg-gray-400">{isUpdatingMarunageOption ? "処理中..." : "－ 丸投げオプションを削除する"}</button>
                                            ) : (
                                                <button onClick={handleAddMarunageOption} disabled={isUpdatingAddonMutation || addonMutationControlsLocked} className="w-full sm:w-auto bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 disabled:bg-gray-400">{isUpdatingMarunageOption ? "処理中..." : "＋ 丸投げオプションを追加する"}</button>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="bg-slate-50 p-4 sm:p-6 rounded-lg border border-slate-200">
                                        <h3 className="font-semibold text-slate-800">お支払い情報・請求履歴</h3>
                                        <p className="text-sm text-slate-500 mt-1 mb-4">ご登録のクレジットカード情報の変更や、過去の請求履歴の確認はこちらから行えます。</p>
                                        <button onClick={handleManagePaymentInfo} disabled={portalLoading || addonMutationControlsLocked} className="w-full sm:w-auto bg-white text-slate-700 border border-slate-300 px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-50 disabled:bg-slate-300">{portalLoading ? "処理中..." : "お支払い情報を管理する"}</button>
                                    </div>
                                </div>
                            )}

                            {isEnglishBeta && (
                                <div className="space-y-4">
                                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                                        <h2 className="text-lg font-bold text-slate-900">Billing, cancellation, and legal</h2>
                                        <p className="mt-2 text-sm leading-6 text-slate-600">
                                            English Operator Beta is a paid-only limited operator beta. There is no downgrade-to-free flow. If canceled, the account becomes inactive unless the operator subscribes again. USD prices are tax-exclusive. Tax collection is not enabled for the initial English Operator Beta unless otherwise required.
                                        </p>
                                        {canManageEnglishBetaBilling && (
                                            <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
                                                <h3 className="text-sm font-bold text-slate-900">Manage billing and cancellation</h3>
                                                <p className="mt-1 text-sm leading-6 text-slate-600">
                                                    Open Stripe Customer Portal to update your payment method, view invoices, or cancel the subscription.
                                                </p>
                                                <button
                                                    onClick={handleManagePaymentInfo}
                                                    disabled={portalLoading || addonMutationControlsLocked}
                                                    className="mt-3 rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800 disabled:bg-slate-400"
                                                >
                                                    {portalLoading ? "Opening portal..." : "Manage Billing / Cancel Subscription"}
                                                </button>
                                            </div>
                                        )}
                                        {canManageBilling && isEnglishBetaCanceled && (
                                            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
                                                <h3 className="text-sm font-bold text-amber-900">Subscription canceled</h3>
                                                <p className="mt-1 text-sm leading-6 text-amber-800">
                                                    The account is inactive. Choose execution capacity to create a new checkout session and resume the English Operator Beta.
                                                </p>
                                                <button
                                                    onClick={() => navigate('/plan-selection?surface=english_beta')}
                                                    className="mt-3 rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800"
                                                >
                                                    Choose execution capacity
                                                </button>
                                            </div>
                                        )}
                                        <p className="mt-3 text-xs leading-5 text-slate-500">
                                            Review the English <a href={ENGLISH_BETA_TERMS_URL} target="_blank" rel="noopener noreferrer" className="font-bold underline hover:text-slate-700">Terms of Service</a>, <a href={ENGLISH_BETA_PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="font-bold underline hover:text-slate-700">Privacy Policy</a>, and <a href={ENGLISH_BETA_SCTA_URL} target="_blank" rel="noopener noreferrer" className="font-bold underline hover:text-slate-700">commercial disclosure summary</a>. The Japanese legal documents remain authoritative.
                                        </p>
                                        <a href={ENGLISH_BETA_LEGAL_SUMMARY_URL} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800">
                                            Read legal overview
                                        </a>
                                    </div>

                                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                                        <h2 className="text-lg font-bold text-slate-900">Beta account support</h2>
                                        <p className="mt-2 text-sm leading-6 text-slate-600">
                                            During the English Operator Beta, billing questions, cancellation requests, and execution lane adjustments can be handled with the SalesBreaker team. The Japanese company database, Japanese outreach copy, and JST execution rules remain Japan-specific.
                                        </p>
                                        <a href="/app/help?surface=english_beta" className="mt-4 inline-flex rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800">
                                            Open Help & Support
                                        </a>
                                    </div>
                                </div>
                            )}

                            {!isEnglishBeta && subscription && !subscription.stripe_subscription_id && !isDisplayedAsFreePlan && (
                                <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                                    <p className="text-sm text-blue-800">プランの変更やご解約に関するお手続きは、担当者まで直接お問い合わせください。</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            <style>{`@keyframes fade-in-out { 0% { opacity: 0; transform: translateY(-20px); } 10% { opacity: 1; transform: translateY(0); } 90% { opacity: 1; transform: translateY(0); } 100% { opacity: 0; transform: translateY(-20px); } } .animate-fade-in-out { animation: fade-in-out 5s ease-in-out forwards; }`}</style>
        </div>
    );
}

export default AccountPage;
